// src/services/paymentsService.js
import { apiRequest, uploadFile } from './api';

export const paymentsService = {
    // Get payment gateways (admin - no country filter)
    getPaymentGateways: async () => {
        try {
            // Don't send country_code parameter to get ALL gateways for admin
            const response = await apiRequest.get('/payments/gateways');
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch payment gateways');
        }
    },

    // Get payment gateways by country (for public/user views) - Updated Route
    getPaymentGatewaysByCountry: async (countryCode) => {
        try {
            // Use the standard endpoint, the backend handles filtering
            const response = await apiRequest.get('/payments/gateways', {
                params: { country_code: countryCode } // Parameter remains the same
            });
            // Extract the payment_gateways array from the response
            // The backend function (after modification) returns it directly under 'payment_gateways'
            return response.data.payment_gateways || [];
        } catch (error) {
            console.error("Error fetching gateways by country:", error); // Add logging
            throw new Error(error.message || `Failed to fetch payment gateways for country: ${countryCode}`);
        }
    },

    // Get payment plans
    getPaymentPlans: async (currency = 'USD') => {
        try {
            const response = await apiRequest.get('/payments/plans', {
                params: { currency }
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch payment plans');
        }
    },

    // Initiate payment
    initiatePayment: async (paymentData) => {
        try {
            const response = await apiRequest.post('/payments/initiate', paymentData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to initiate payment');
        }
    },

    // Verify payment
    verifyPayment: async (verificationData) => {
        try {
            const response = await apiRequest.post('/payments/verify-payment', verificationData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to verify payment');
        }
    },

    // Confirm manual payment (bank transfer)
    confirmManualPayment: async (reference) => {
        try {
            const response = await apiRequest.post('/payments/confirm-manual-payment', {
                payment_reference: reference
            });
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to confirm manual payment');
        }
    },

    // Initialize Paystack payment (client-side)
    initializePaystack: (paymentData, onSuccess, onClose) => {
        if (!window.PaystackPop) {
            throw new Error('Paystack library not loaded');
        }

        const handler = window.PaystackPop.setup({
            key: paymentData.public_key,
            email: paymentData.email,
            amount: paymentData.amount * 100,
            currency: paymentData.currency,
            ref: paymentData.reference,
            callback: onSuccess,
            onClose: onClose
        });

        handler.openIframe();
    },

    // Initialize Razorpay payment (client-side)
    initializeRazorpay: (paymentData, onSuccess, onClose) => {
        if (!window.Razorpay) {
            throw new Error('Razorpay library not loaded');
        }

        const options = {
            key: paymentData.key_id,
            amount: paymentData.amount * 100,
            currency: paymentData.currency,
            name: 'Haazri',
            description: 'License Payment',
            order_id: paymentData.order_id,
            handler: onSuccess,
            modal: {
                ondismiss: onClose
            },
            theme: {
                color: '#3b82f6'
            }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
    },

    // Open payment in new window (Stripe, Flutterwave, etc.)
    openPaymentWindow: (url) => {
        const width = 600;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);

        const paymentWindow = window.open(
            url,
            'PaymentWindow',
            `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        return paymentWindow;
    },

    // Get subscription details
    getSubscription: async () => {
        try {
            const response = await apiRequest.get('/subscriptions');
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch subscription details');
        }
    },

    // Get payment history
    getPaymentHistory: async (params = {}) => {
        try {
            const response = await apiRequest.get('/payments/history', { params });
            // Return the entire data object which includes data array, total, limit, offset
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch payment history');
        }
    },

    // Get payment analytics and subscription status insights
    getPaymentAnalytics: async (params = {}) => {
        try {
            const response = await apiRequest.get('/payments/analytics', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch payment analytics');
        }
    },

    // Create payment gateway (admin only)
    createPaymentGateway: async (gatewayData) => {
        try {
            const response = await apiRequest.post('/payments/gateways', gatewayData);
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create payment gateway');
        }
    },

    // Update payment gateway
    updatePaymentGateway: async (gatewayId, gatewayData) => {
        try {
            const response = await apiRequest.put(`/payments/gateways/${gatewayId}`, gatewayData);
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update payment gateway');
        }
    },

    // Delete payment gateway
    deletePaymentGateway: async (gatewayId) => {
        try {
            const response = await apiRequest.delete(`/payments/gateways/${gatewayId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete payment gateway');
        }
    },

    // Cancel subscription
    cancelSubscription: async (reason = '') => {
        try {
            const response = await apiRequest.post('/subscriptions/cancel', { reason });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to cancel subscription');
        }
    },

    // Cancel pending subscription/payment
    cancelPendingSubscription: async (subscriptionId) => {
        try {
            const response = await apiRequest.post('/subscriptions/cancel-pending', {
                subscription_id: subscriptionId
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to cancel pending subscription');
        }
    },

    // Upgrade/downgrade subscription
    changeSubscription: async (planId) => {
        try {
            const response = await apiRequest.post('/subscriptions/change', { plan_id: planId });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to change subscription');
        }
    },

    // Generate invoice data for one or all organizations over a date range
    generateInvoice: async (params = {}) => {
        try {
            const response = await apiRequest.get('/invoices/generate', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || error.message || 'Failed to generate invoice');
        }
    },

    // Core Admin: extend an organization's subscription by N days
    extendSubscription: async (orgId, newEndDate, reason = '') => {
        try {
            const response = await apiRequest.post('/subscriptions/extend', {
                org_id: orgId,
                new_end_date: newEndDate,
                reason
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to extend subscription');
        }
    },

    // Get all license pricing tiers (admin management view - every tier, active or not)
    getLicensePricing: async () => {
        try {
            const response = await apiRequest.get('/payments/license-pricing');
            return response.data.data || [];
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch license pricing');
        }
    },

    // Create a license pricing tier (core admin only)
    createLicensePricing: async (data) => {
        try {
            const response = await apiRequest.post('/payments/license-pricing', data);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create pricing tier');
        }
    },

    // Update a license pricing tier (core admin only)
    updateLicensePricing: async (id, data) => {
        try {
            const response = await apiRequest.put(`/payments/license-pricing/${id}`, data);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update pricing tier');
        }
    },

    // Delete a license pricing tier (core admin only)
    deleteLicensePricing: async (id) => {
        try {
            const response = await apiRequest.delete(`/payments/license-pricing/${id}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete pricing tier');
        }
    },

    // Bulk upload license pricing tiers via CSV (core admin only) - upserts by
    // country_code + user_count, so re-uploading an updated sheet is safe.
    bulkUploadLicensePricing: async (file, onProgress = null) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await uploadFile('/payments/license-pricing/bulk-upload', formData, onProgress);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to bulk upload license pricing');
        }
    }
};
